import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { RobotIcon, SmileyIcon, SparkleIcon, UploadIcon, CloseIcon } from "../Auth/onboarding/icons";
import { ChatBubbleIcon, SearchIcon } from "../Dashboard/icons";
import { ImageIcon } from "./icons";
import LauncherGlyph from "./LauncherGlyph";
import {
  SUGGESTED_ICONS,
  searchIcons,
  humanizeIconName,
  toStoredValue,
  isLucideIcon,
  lucideNameOf,
  loadIconMap,
} from "./launcherIcons";
import { uploadFile } from "../../actions/uploads";

// Kept so assistants saved before the icon library still render their choice.
const LEGACY_STYLES = {
  bubble: { icon: <ChatBubbleIcon />, label: "Chat bubble" },
  smile: { icon: <SmileyIcon />, label: "Smile icon" },
  spark: { icon: <SparkleIcon />, label: "Spark icon" },
  logo: { icon: <ImageIcon />, label: "Custom logo" },
};

const QUICK_PICKS = SUGGESTED_ICONS.slice(0, 5);

export default function LauncherIconCard({ settings, onChange }) {
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const pickerRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  // The full library arrives as its own chunk the first time the picker opens.
  const [iconMap, setIconMap] = useState(null);

  const selected = settings.launcherStyle;
  const legacy = LEGACY_STYLES[selected];
  const selectedName = lucideNameOf(selected);
  const results = searchIcons(iconMap, query);

  useEffect(() => {
    if (!pickerOpen || iconMap) return;
    loadIconMap().then(setIconMap);
  }, [pickerOpen, iconMap]);

  // A picker that stays open after clicking away feels broken.
  useEffect(() => {
    if (!pickerOpen) return undefined;

    const onPointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) setPickerOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPickerOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen]);

  // The file has to reach the server before it is any use: avatarUrl is saved
  // on the assistant and served to real visitors, so a local object URL would
  // be meaningless outside this browser tab.
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset so re-picking the same file still fires a change event.
    e.target.value = "";
    if (!file) return;

    setUploadError("");
    setUploading(true);

    const result = await dispatch(uploadFile(file));

    setUploading(false);

    if (!result?.success) {
      setUploadError(result?.message || "We could not upload that image.");
      return;
    }

    onChange("avatarImage", result.url);
    // An uploaded image is the launcher now, so the icon choice steps aside.
    onChange("launcherStyle", "logo");
  };

  const pick = (name) => {
    onChange("launcherStyle", toStoredValue(name));
    // An icon and an uploaded image are the same slot: the image takes
    // precedence everywhere it renders, so leaving it set would make the icon
    // choice do nothing visible.
    if (settings.avatarImage) onChange("avatarImage", null);
    setPickerOpen(false);
    setQuery("");
  };

  const browsingSelection = isLucideIcon(selected) && !QUICK_PICKS.includes(selectedName);

  return (
    <section className="dash-card cz-card">
      <h2 className="cz-card-title">3. Launcher &amp; icon</h2>

      <div className="cz-launcher-row">
        <div className="cz-field cz-launcher-styles">
          <span className="cz-field-label">Launcher style</span>

          <div className="cz-tile-row cz-icon-quick">
            {QUICK_PICKS.map((name) => (
              <button
                type="button"
                key={name}
                className={`cz-tile cz-icon-tile${selected === toStoredValue(name) ? " is-active" : ""}`}
                onClick={() => pick(name)}
                title={humanizeIconName(name)}
                aria-label={humanizeIconName(name)}
              >
                <LauncherGlyph value={toStoredValue(name)} />
              </button>
            ))}

            <div className="cz-icon-picker-wrap" ref={pickerRef}>
              <button
                type="button"
                className={`cz-tile cz-icon-tile cz-icon-more${browsingSelection ? " is-active" : ""}`}
                onClick={() => setPickerOpen((open) => !open)}
                aria-expanded={pickerOpen}
              >
                {/* Shows the current pick when it isn't one of the five. */}
                {browsingSelection ? (
                  <LauncherGlyph value={selected} fallback={<SearchIcon />} />
                ) : (
                  <SearchIcon />
                )}
                <span>Browse</span>
              </button>

              {pickerOpen && (
                <div className="cz-icon-picker">
                  <label className="cz-icon-search">
                    <SearchIcon />
                    <input
                      type="search"
                      value={query}
                      placeholder="Search 1,700+ icons"
                      onChange={(e) => setQuery(e.target.value)}
                      aria-label="Search icons"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="cz-icon-picker-close"
                      onClick={() => setPickerOpen(false)}
                      aria-label="Close icon picker"
                    >
                      <CloseIcon />
                    </button>
                  </label>

                  {!iconMap && query.trim() ? (
                    <p className="cz-icon-empty">Loading icons&hellip;</p>
                  ) : results.length === 0 ? (
                    <p className="cz-icon-empty">
                      No icons match &ldquo;{query.trim()}&rdquo;.
                    </p>
                  ) : (
                    <div className="cz-icon-grid">
                      {results.map((name) => (
                        <button
                          type="button"
                          key={name}
                          className={`cz-icon-option${selected === toStoredValue(name) ? " is-active" : ""}`}
                          onClick={() => pick(name)}
                          title={humanizeIconName(name)}
                          aria-label={humanizeIconName(name)}
                        >
                          <LauncherGlyph value={toStoredValue(name)} size={20} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* <p className="cz-icon-current">
            {selectedName
              ? humanizeIconName(selectedName)
              : legacy?.label || "Pick an icon"}
          </p> */}
        </div>

        <div className="cz-field cz-avatar-field">
          <span className="cz-field-label">Avatar / icon</span>
          <div className="cz-avatar-row">
            {/* An uploaded image is the avatar in full — no colour fill behind
                it, which would otherwise show through a transparent PNG. */}
            <span
              className="cz-avatar-preview"
              style={
                settings.avatarImage
                  ? undefined
                  : { background: settings.primaryColor }
              }
            >
              {settings.avatarImage ? (
                <img src={settings.avatarImage} alt="Assistant avatar" />
              ) : isLucideIcon(selected) ? (
                <LauncherGlyph value={selected} fallback={<RobotIcon />} />
              ) : (
                legacy?.icon || <RobotIcon />
              )}
            </span>
            <button
              type="button"
              className="cz-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <span className="install-status-spinner" aria-hidden="true" />
              ) : (
                <UploadIcon />
              )}
              {uploading ? "Uploading..." : "Upload"}
              <span>PNG, SVG &middot; max 2MB</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/svg+xml,image/jpeg,image/webp"
              hidden
              onChange={handleFile}
            />
          </div>

          {uploadError && <p className="auth-error">{uploadError}</p>}

          {settings.avatarImage && !uploading && (
            <button
              type="button"
              className="auth-inline-link cz-avatar-clear"
              onClick={() => {
                setUploadError("");
                onChange("avatarImage", null);
              }}
            >
              Remove image
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
